import type { FileBody } from '@/shared/api/generated/model';
import { appConfig } from '@/shared/config';

/** Полный адрес содержимого: путь из API относителен корня API. */
export function fileUrl(file: Pick<FileBody, 'url'>, download = false): string {
  // Картинка по внешней ссылке из описания — не наш файл, адрес как есть.
  if (/^(https?:)?\/\//.test(file.url) || file.url.startsWith(appConfig.apiBaseUrl)) {
    return file.url;
  }

  const base = appConfig.apiBaseUrl.replace(/\/$/, '');

  return `${base}${file.url}${download ? '?download=1' : ''}`;
}

/** «1,2 МБ», «340 КБ», «12 Б». */
export function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} Б`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} КБ`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} МБ`;
}

export function isImage(file: Pick<FileBody, 'mime'>): boolean {
  return file.mime.startsWith('image/') && file.mime !== 'image/svg+xml';
}

export type FileKind = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'other';

/** Как показывать: что браузер нарисует сам, что покажем текстом, что — карточкой. */
export function fileKind(file: Pick<FileBody, 'mime' | 'name'>): FileKind {
  const mime = file.mime;

  if (isImage(file)) {
    return 'image';
  }

  if (mime.startsWith('video/')) {
    return 'video';
  }

  if (mime.startsWith('audio/')) {
    return 'audio';
  }

  if (mime === 'application/pdf') {
    return 'pdf';
  }

  if (
    (mime.startsWith('text/') && mime !== 'text/html') ||
    mime === 'application/json' ||
    /\.(md|txt|csv|json|ya?ml|xml|log|ts|tsx|js|jsx|go|py|sql|sh|css|scss|html)$/i.test(file.name)
  ) {
    return 'text';
  }

  return 'other';
}

/** Короткая подпись типа для карточки: «PDF», «DOCX», «ZIP». */
export function fileLabel(file: Pick<FileBody, 'mime' | 'name'>): string {
  const ext = file.name.split('.').pop();

  if (ext !== undefined && ext !== file.name && ext.length <= 5) {
    return ext.toUpperCase();
  }

  return file.mime.split('/')[1]?.toUpperCase() ?? 'FILE';
}
